// Companies (platform admin) — every signed-up company: name, country, owner, created, demo
// flag and the activation/billing state (current subscription). Fleshed out (W9) from a bare
// read-only grid to a commercial admin surface: a KPI band (total · paying · trialing · past-due
// · demo), subscription-status + demo filter chips (server-side, via the list endpoint's new
// filter params), client-side sortable columns, and a click-through detail drawer that drills to
// the owner's user page (where Impersonate lives) and the company's chart of accounts. Reference:
// Kyntor accountant-clients / super-admin patterns + standard SaaS-admin conventions.
import { html, signal, navigate } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { shell } from './shell.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { icon } from '../shared/icons.js';
import { focusModal } from '../shared/modal.js';
import { sortTh, nextSort, sortRows } from '../shared/sortcols.js';
import { isPartnerAdmin, isPlatformAdmin } from '../shared/auth.js';
const _regionalPartner = () => isPartnerAdmin() && !isPlatformAdmin();

export const SUB_BADGE = { active: 'success', trialing: 'info', cancelled: 'danger',
                           past_due: 'warning', expired: 'secondary' };

// Subscription-status filter chips (value sent as ?subscription_status; 'none' = no subscription
// row). Kept as data so the KPI band and the chip row stay in lock-step.
export const SUB_FILTERS = [
  { key: '',         label: 'All' },
  { key: 'active',   label: 'Active' },
  { key: 'trialing', label: 'Trialing' },
  { key: 'past_due', label: 'Past due' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'none',     label: 'No plan' },
];

// Value a sortable column reads off a row — client-side / current-page only (GET /admin/companies
// has no sort= param), same contract as the Users grid.
export function companySortVal(row, key){
  if(key === 'name') return (row.name || '').toLowerCase();
  if(key === 'country') return (row.country || '').toLowerCase();
  if(key === 'owner') return (row.owner_email || '').toLowerCase();
  if(key === 'created') return row.created || '';
  if(key === 'tier') return (row.tier || '').toLowerCase();
  if(key === 'billing') return (row.subscription_status || '~');   // '~' sorts "none" last
  return '';
}

// Build the querystring for a companies list request from the current controls — one place so the
// KPI counts and the list use identical filter semantics.
export function companiesParams({ perPage, page, q, sub, demoOnly }){
  const p = new URLSearchParams({ per_page: String(perPage), page: String(page) });
  if(q && q.trim()) p.set('q', q.trim());
  if(sub) p.set('subscription_status', sub);
  if(demoOnly) p.set('is_demo', 'true');
  return p;
}

export function companiesScreen(){
  const rows = signal([]), total = signal(0), page = signal(1), pages = signal(1),
        perPage = signal(DEFAULT_PER_PAGE), loading = signal(true);
  const sub = signal(''), demoOnly = signal(false);      // active filter chips
  const sort = signal({ key: '', dir: 'asc' });
  const stats = signal(null);                            // KPI counts (platform-wide)
  const selected = signal(null);                         // company row shown in the detail drawer
  // delete state — the company being deleted (null = modal closed), the typed "DELETE" confirm,
  // the in-flight flag, and the error shown in the modal.
  const delCo = signal(null), delText = signal(''), deleting = signal(false), delErr = signal('');
  let timer, lastQ = '', loadSeq = 0;

  async function load(){
    const seq = ++loadSeq;
    loading.value = true;
    const p = companiesParams({ perPage: perPage.value, page: page.value, q: lastQ,
                                sub: sub.value, demoOnly: demoOnly.value });
    try { const r = await api.get('/admin/companies?' + p);
          if(seq !== loadSeq) return;
          rows.value = r.data || []; total.value = r.total || 0; pages.value = r.total_pages || 1; }
    catch { if(seq !== loadSeq) return; rows.value = []; total.value = 0; pages.value = 1; }
    if(seq === loadSeq) loading.value = false;
  }

  // KPI band — platform-wide counts via the same list endpoint (per_page=1 → total), exactly the
  // admin dashboard's pattern. Fired once on mount; a filter chip narrows the LIST, not the cards.
  async function loadStats(){
    const count = qs => api.get('/admin/companies?per_page=1&' + qs).then(r => (r && r.total) || 0).catch(() => null);
    const [all, active, trialing, past_due, demo] = await Promise.all([
      count(''), count('subscription_status=active'), count('subscription_status=trialing'),
      count('subscription_status=past_due'), count('is_demo=true'),
    ]);
    stats.value = { all, active, trialing, past_due, demo };
  }
  load(); loadStats();

  function onSearch(e){ lastQ = e.target.value; page.value = 1; clearTimeout(timer); timer = setTimeout(load, 220); }
  function onPer(e){ perPage.value = e.target.value; page.value = 1; load(); }
  function goPage(p){ page.value = p; load(); }
  function onSort(key){ sort.value = nextSort(sort.value, key, key === 'created' ? 'desc' : 'asc'); }
  function setSub(k){ sub.value = k; page.value = 1; load(); }
  function toggleDemo(){ demoOnly.value = !demoOnly.value; page.value = 1; load(); }

  function openDetail(c){ selected.value = c; focusModal(); }
  function closeDetail(){ selected.value = null; }
  function viewOwner(c){ if(c && c.owner_id){ closeDetail(); navigate('/users/' + c.owner_id); } }
  function openAccounts(c){ if(c){ closeDetail(); navigate('/companies/' + c.id + '/accounts'); } }

  // delete a company — type-to-confirm modal: are-you-sure + must type DELETE in UPPERCASE before
  // the red Delete enables. DESTRUCTIVE: removes the company AND all its books (demo or real). The
  // 🗑 on a row opens it (stops propagation so it never opens the detail drawer).
  const delCoConfirmed = () => delText.value.trim() === 'DELETE';
  function askDeleteCo(c){ delCo.value = c; delText.value = ''; delErr.value = ''; focusModal(); }
  function closeDeleteCo(){ delCo.value = null; delText.value = ''; delErr.value = ''; }
  async function confirmDeleteCo(){
    const c = delCo.value;
    if(!c || !delCoConfirmed()) return;                  // never fire without the typed confirmation
    deleting.value = true; delErr.value = '';
    try {
      await api.del('/admin/companies/' + c.id);
      closeDeleteCo();
      if(selected.value && selected.value.id === c.id) selected.value = null;   // close its drawer too
      await load(); loadStats();                          // refresh list + KPI counts
    } catch(err){
      delErr.value = (err.data && err.data.error) || err.message || 'Delete failed';
    }
    deleting.value = false;
  }

  const subBadge = s => s
    ? html`<span class=${'badge badge-' + (SUB_BADGE[s] || 'secondary')}>${s === 'trialing' ? 'Trial' : s}</span>`
    : html`<span class="badge badge-secondary">none</span>`;

  const kpiBand = () => {
    const s = stats.value;
    const fmt = v => (v === null || v === undefined) ? '—' : String(v);
    const cards = [
      { label: 'Companies',    val: s ? fmt(s.all) : '…',       sub: 'on the platform',    accent: 'primary' },
      { label: 'Paying',       val: s ? fmt(s.active) : '…',    sub: 'active subscription', accent: 'success' },
      { label: 'Trialing',     val: s ? fmt(s.trialing) : '…',  sub: 'in trial',            accent: 'info' },
      { label: 'Past due',     val: s ? fmt(s.past_due) : '…',  sub: 'payment overdue',     accent: 'warning' },
      { label: 'Demo',         val: s ? fmt(s.demo) : '…',      sub: 'sandbox companies',   accent: 'dark' },
    ];
    return html`<div class="kpi-grid">${cards.map(c => html`
      <div class=${'kpi accent-' + c.accent}>
        <div class="card-body">
          <div class="kpi-label">${c.label}</div>
          <div class="kpi-val">${c.val}</div>
          <div class="kpi-sub">${c.sub}</div>
        </div>
      </div>`)}</div>`;
  };

  const list = () => {
    if(loading.value) return html`<div class="muted-block">Loading…</div>`;
    if(!rows.value.length) return html`<div class="muted-block">No companies match.</div>`;
    const cur = sort.value;
    const sorted = sortRows(rows.value, cur, companySortVal);
    const pg = () => pager({ page: page.value, totalPages: pages.value, total: total.value,
                             perPage: Number(perPage.value), onPage: goPage });
    return html`<div class="list-wrap">
      ${pg()}
      <div class="table-wrap"><table class="cb-table adm-grid">
        <thead><tr>
          ${sortTh('Company', 'name', cur, onSort)}
          ${sortTh('Country', 'country', cur, onSort)}
          ${sortTh('Owner', 'owner', cur, onSort)}
          ${sortTh('Created', 'created', cur, onSort)}
          ${sortTh('Tier', 'tier', cur, onSort)}
          ${sortTh('Billing', 'billing', cur, onSort)}
          <th></th>
          <th></th>
        </tr></thead>
        <tbody>${sorted.map(c => html`<tr class="adm-row" @click=${() => openDetail(c)}>
          <td><b>${c.name}</b>${c.is_demo ? html` <span class="badge badge-warning">DEMO</span>` : ''}</td>
          <td>${c.country || '—'}</td>
          <td>${c.owner_email
            ? html`${c.owner_name || ''}<span class="row-sub adm-owner-mail">${c.owner_email}</span>`
            : html`<span class="muted">no owner yet</span>`}</td>
          <td class="mono">${c.created || '—'}</td>
          <td>${c.tier || '—'}</td>
          <td>${subBadge(c.subscription_status)}</td>
          <td class="ta-r nowrap adm-del-cell"><button type="button" class="btn-row-del"
            title="Delete company" aria-label="Delete company" ?hidden=${_regionalPartner()}
            @click=${e => { e.stopPropagation(); askDeleteCo(c); }}><span class="del-ico" aria-hidden="true">${icon('trash-2', {size: 14})}</span></button></td>
          <td class="row-chevron">${icon('chevron-right', {size: 14})}</td>
        </tr>`)}</tbody>
      </table></div>
      <div class="form-note user-sort-note">Sorting applies to the current page only.</div>
      ${pg()}
    </div>`;
  };

  // Detail drawer body — read `selected` at the top (no nested reactive slots).
  const detail = () => {
    const c = selected.value;
    if(!c) return '';
    const rowsKV = [
      ['Country', c.country || '—'],
      ['Created', c.created || '—'],
      ['Status', c.status || '—'],
      ['Tier', c.tier || '—'],
      ['Billing period', c.billing_period || '—'],
      ['Provider', c.provider || '—'],
      ['Licence payer', c.license_payer || '—'],
      ['Demo', c.is_demo ? 'Yes' : 'No'],
    ];
    return html`<div class="modal-head">
        <h3>${c.name}${c.is_demo ? html` <span class="badge badge-warning">DEMO</span>` : ''}</h3>
        <button type="button" class="modal-x" @click=${closeDetail}>✕</button>
      </div>
      <div class="adm-detail-owner">
        <div class="kpi-label">Owner</div>
        ${c.owner_email
          ? html`<div><b>${c.owner_name || c.owner_email}</b><div class="row-sub">${c.owner_email}</div></div>`
          : html`<span class="muted">No owner assigned yet.</span>`}
      </div>
      <div class="adm-detail-kv">
        ${rowsKV.map(([k, v]) => html`<div class="adm-kv-row"><span class="adm-kv-k">${k}</span><span class="adm-kv-v">${v}</span></div>`)}
        <div class="adm-kv-row"><span class="adm-kv-k">Subscription</span><span class="adm-kv-v">${subBadge(c.subscription_status)}</span></div>
      </div>
      <div class="form-actions">
        <button type="button" class="btn btn-outline" @click=${closeDetail}>Close</button>
        <button type="button" class="btn btn-outline" ?disabled=${!c.owner_id} @click=${() => openAccounts(c)}>Chart of accounts</button>
        <button type="button" class="btn btn-primary" ?disabled=${!c.owner_id} @click=${() => viewOwner(c)}>View owner →</button>
      </div>`;
  };

  const content = html`<div class="page">
    <div class="page-head with-action">
      <div><h1 class="page-title">Companies</h1>
        <div class="page-sub">${() => total.value + (total.value === 1 ? ' company' : ' companies')} shown</div></div>
    </div>
    ${kpiBand}
    <div class="toolbar">
      <input class="form-control" type="search" placeholder="Search… e.g. Riverside or callan@"
        @input=${onSearch}>
      ${perPageSelect(perPage.value, onPer)}
    </div>
    <div class="adm-chips">
      ${() => SUB_FILTERS.map(f => html`<button type="button"
        class=${'btn btn-sm ' + (sub.value === f.key ? 'btn-primary' : 'btn-outline')}
        @click=${() => setSub(f.key)}>${f.label}</button>`)}
      <button type="button" class=${() => 'btn btn-sm adm-chip-demo ' + (demoOnly.value ? 'btn-primary' : 'btn-outline')}
        @click=${toggleDemo}>Demo only</button>
    </div>
    ${list}

    <div class="modal-overlay" ?hidden=${() => !selected.value}>
      <div class="modal-card">${detail}</div>
    </div>

    <div class="modal-overlay del-company-modal" ?hidden=${() => !delCo.value}>
      <div class="modal-card">
        <div class="modal-head"><h3>Delete company?</h3><button type="button" class="modal-x" @click=${closeDeleteCo}>✕</button></div>
        <p class="form-note">You're about to permanently delete
          <b>${() => delCo.value ? delCo.value.name : ''}</b> and <b>all of its books</b> — invoices,
          journals, bank data, contacts and its subscription. This cannot be undone.</p>
        <label class="form-label">Type <b>DELETE</b> to confirm</label>
        <input class="form-control mb-2" autocomplete="off" spellcheck="false" placeholder="DELETE"
               .value=${delText} @input=${e => { delText.value = e.target.value; }}>
        ${() => delErr.value ? html`<div class="alert alert-danger py-2 mb-2">${delErr.value}</div>` : null}
        <div class="form-actions">
          <button type="button" class="btn btn-outline" @click=${closeDeleteCo}>Cancel</button>
          <button type="button" class="btn btn-danger" ?disabled=${() => deleting.value || !delCoConfirmed()}
                  @click=${confirmDeleteCo}>${() => deleting.value ? 'Deleting…' : 'Delete company'}</button>
        </div>
      </div>
    </div>
  </div>`;
  return shell('companies', 'Companies', content);
}
